import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { PAYMENT_PROVIDERS, TRANSACTION_PAYMENT_OPTIONS } from '@medipass/utils/constants';
import sdk from '@medipass/web-sdk';
import { ApplePayResponse } from '@medipass/web-sdk/lib/resources/payments';

export type ApplePayAvailabilityStatus = 'unsupported' | 'checking' | 'eligible' | 'ineligible';
export type ApplePayPaymentRequest = ApplePayResponse['paymentRequest'];

type UseApplePayAvailabilityProps = {
  amount?: string;
  enabled?: boolean;
};

type GetCheckoutPaymentOptionProps = {
  applePayStatus: ApplePayAvailabilityStatus;
  fallbackPaymentOption?: string;
  hasUserSelectedPaymentOption?: boolean;
  paymentOption: string;
  showExternalPaymentProviders?: boolean;
};

export const getInitialApplePayAvailabilityStatus = (): ApplePayAvailabilityStatus => {
  try {
    if (!window.ApplePaySession) return 'unsupported';
    if (!window.ApplePaySession.supportsVersion(3)) return 'unsupported';
    if (!window.ApplePaySession.canMakePayments()) return 'unsupported';

    return 'checking';
  } catch (err) {
    return 'unsupported';
  }
};

export const getCheckoutPaymentOption = ({
  applePayStatus,
  fallbackPaymentOption = TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD,
  hasUserSelectedPaymentOption = false,
  paymentOption,
  showExternalPaymentProviders = false,
}: GetCheckoutPaymentOptionProps) => {
  if (!showExternalPaymentProviders) {
    return paymentOption === TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY ? fallbackPaymentOption : paymentOption;
  }

  if (applePayStatus === 'checking') {
    return hasUserSelectedPaymentOption ? paymentOption : TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY;
  }

  if (applePayStatus === 'eligible') {
    return hasUserSelectedPaymentOption ? paymentOption : TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY;
  }

  if (paymentOption === TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY) {
    return fallbackPaymentOption;
  }

  return paymentOption;
};

export const useApplePayAvailability = ({ amount = '', enabled = true }: UseApplePayAvailabilityProps) => {
  const initialStatus = React.useMemo(
    () => (enabled ? getInitialApplePayAvailabilityStatus() : 'unsupported'),
    [enabled],
  );

  const getNonceQuery = useQuery({
    queryKey: ['apple-pay-nonce', amount],
    queryFn: async () => {
      const data = await sdk.payments.getNonce({
        amount: amount?.replace('$', '')?.replace(/,/g, ''),
        paymentProvider: PAYMENT_PROVIDERS.APPLE_PAY,
        isSelfCheckoutSession: true,
      });

      return data as ApplePayResponse;
    },
    enabled: initialStatus === 'checking',
  });

  const paymentRequest = (getNonceQuery.data as ApplePayResponse | undefined)?.paymentRequest;

  let status = initialStatus;
  if (paymentRequest) {
    status = (getNonceQuery.data as ApplePayResponse).canMakeApplePayment ? 'eligible' : 'ineligible';
  } else if (getNonceQuery.isError) {
    status = 'ineligible';
  } else if (initialStatus === 'checking' && getNonceQuery.fetchStatus !== 'idle') {
    status = 'checking';
  }

  return {
    paymentRequest,
    status,
  };
};
